import Papa, { ParseResult } from "papaparse";

import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";
import { detectCsvDelimiter } from "../../services/UtilityService";
import { EphemeralImportFile } from "./EphemeralImport.d";

const REQUIRED_COLUMNS = ["scenario", "proposition_01", "proposition_02", "answer"];

export const ALL_PROPOSITION_KEYS = [
  "proposition_01",
  "proposition_02",
  "proposition_03",
  "proposition_04",
  "proposition_05",
  "proposition_06",
  "proposition_07",
] as const;

const MIN_PROPOSITIONS = 2;
export const MAX_PROPOSITIONS = ALL_PROPOSITION_KEYS.length;

export const parseCsvFile = (file: File, setCsvData: Function, language: string) => {
  detectCsvDelimiter(file).then((delimiter) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      delimiter,
      complete: (results: ParseResult<Record<string, string>>) => {
        if (results.errors.length > 0) {
          console.error("CSV Parse Errors:", results.errors);
          alert(translate(language, "PAGES.EPHEMERAL_IMPORT.ERROR_CSV_IMPORT"));
          return;
        }

        const fields = results.meta.fields || [];
        if (REQUIRED_COLUMNS.some((col) => !fields.includes(col))) {
          alert(translate(language, "PAGES.EPHEMERAL_IMPORT.ERRORS.MISSING_COLUMNS"));
          return;
        }

        const presentPropositions = ALL_PROPOSITION_KEYS.filter((key) => fields.includes(key));
        if (presentPropositions.length < MIN_PROPOSITIONS) {
          alert(translate(language, "PAGES.EPHEMERAL_IMPORT.ERRORS.MISSING_COLUMNS"));
          return;
        }

        setCsvData(
          results.data.map((row, index) => ({
            scenario: row.scenario || "",
            propositions: presentPropositions.map((key) => row[key] || ""),
            answer: row.answer || "",
            _index: index,
          }))
        );
      },
      error: (error: Error) => {
        console.error("Error parsing CSV:", error);
        alert(`${translate(language, "PAGES.EPHEMERAL_IMPORT.ERROR_CSV_IMPORT")} ${error.message}`);
      },
    });
  });
};

export const submitEphemeralExercisesImport = (
  data: Array<EphemeralImportFile>,
  language: string,
  setSubmitting: Function,
  showAlert: Function,
  popAlert: Function,
  setCsvData: Function
) => {
  setSubmitting(true);

  const csvContent = Papa.unparse(
    data.map(({ _index, propositions, scenario, answer }) => ({
      scenario,
      ...Object.fromEntries(
        ALL_PROPOSITION_KEYS.slice(0, propositions.length).map((key, index) => [key, propositions[index]])
      ),
      answer,
    })),
    { delimiter: ";" }
  );

  const formData = new FormData();
  formData.append("file", new Blob([csvContent], { type: "text/csv" }), "ephemeral_exercises.csv");

  showAlert?.({
    type: "info",
    title: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.LOADING_TITLE"),
    message: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.LOADING_MESSAGE"),
    clickOutsideToClose: false,
    buttons: [],
  });

  ApiService.post("ephemeral-evaluations/exercises/batch", formData)
    .then((response) => {
      popAlert?.();
      showAlert?.({
        type: "success",
        title: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.SUCCESS_TITLE"),
        message: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.SUCCESS_MESSAGE").replace(
          "{{COUNT}}",
          String(response?.data?.created_ephemeral_exercises?.length ?? data.length)
        ),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.BUTTON_OK"),
            onClick: () => {
              popAlert?.();
              setCsvData([]);
            },
          },
        ],
      });
    })
    .catch((err) => {
      popAlert?.();

      const errData = (err as any)?.data;

      showAlert?.({
        type: "danger",
        title: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.API_ERROR"),
        message:
          errData?.error?.message ||
          errData?.message ||
          translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.ERROR_MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.EPHEMERAL_IMPORT.DST_ALERTS.BUTTON_CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: errData ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(errData, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => setSubmitting(false));
};
